import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    // company: '',
    // service: '',
    // budget: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  
  // EmailJS Configuration
  // Replace these with your EmailJS credentials from https://www.emailjs.com/
  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
  const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

  // Phone validation function for Indian phone numbers
  const validatePhone = (phone: string): boolean => {
    // Remove all spaces, dashes, and special characters except + and digits
    const cleaned = phone.replace(/[\s\-\(\)]/g, '');
    
    // Check if it starts with +91 or 91, then remove country code
    let digits = cleaned;
    if (cleaned.startsWith('+91')) {
      digits = cleaned.substring(3);
    } else if (cleaned.startsWith('91') && cleaned.length > 10) {
      digits = cleaned.substring(2);
    }
    
    // Should be exactly 10 digits and start with 6, 7, 8, or 9
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(digits);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const value = e.target.value;
    const name = e.target.name;
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Validate phone number in real-time
    if (name === 'phone') {
      if (value && !validatePhone(value)) {
        setPhoneError('Please enter a valid Indian phone number (10 digits starting with 6, 7, 8, or 9)');
      } else {
        setPhoneError(null);
      }
    }
    
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validate phone number before submission
    if (formData.phone && !validatePhone(formData.phone)) {
      setPhoneError('Please enter a valid Indian phone number (10 digits starting with 6, 7, 8, or 9)');
      setIsLoading(false);
      return;
    }

    try {
      // Prepare template parameters
      const templateParams = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        // company: formData.company || 'Not provided',
        // service: formData.service,
        // budget: formData.budget || 'Not specified',
        message: formData.message,
        to_email: 'contact@colordroponline.com', // Your receiving email
      };

      // Send email using EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      // Success
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        // company: '',
        // service: '',
        // budget: '',
        message: ''
      });
      setPhoneError(null);

      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('EmailJS Error:', err);
      setError('Something went wrong. Please try again or contact us directly at contact@colordroponline.com');
    } finally {
      setIsLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: `+91 99992 06699 | +91 73142 66699`,
      description: 'Mon-Sat 10AM-7PM IST'
    },
    {
      icon: Mail,
      title: 'Email',
      details: 'contact@colordroponline.com',
      description: 'We respond within 24 hours'
    },
    {
      icon: MapPin,
      title: 'Office',
      details: '305, Veda Business Park, Bhawarkua, Indore',
      description: 'Madhya Pradesh, 452001'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: 'Monday - Saturday',
      description: 'Mon-Sat 10AM-7PM IST'
    }
  ];

  const services = [
    'Digital Marketing',
    'Web Development',
    'App Development',
    'Consultation',
    'Other'
  ];

  const budgetRanges = [
    '$5,000 - $15,000',
    '$15,000 - $35,000',
    '$35,000 - $75,000',
    '$75,000+',
    'Not sure yet'
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-bold mb-6">
              Let's Create Something
              <br />
              <span className="text-gradient">Extraordinary</span>
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed">
              Ready to elevate your luxury brand's digital presence? Let's discuss your project and how we can help you achieve exceptional results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: false }}
            >
              <h2 className="text-3xl font-serif font-bold mb-8">Get Your Free Quote</h2>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-50 border border-green-200 rounded-lg p-8 text-center"
                >
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-green-800 mb-2">Thank You!</h3>
                  <p className="text-green-600">We've received your message and will get back to you within 24 hours.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3"
                    >
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-red-700 text-sm">{error}</p>
                    </motion.div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-300"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-300"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-neutral-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-300 ${
                        phoneError ? 'border-red-300 focus:ring-red-500' : 'border-neutral-300'
                      }`}
                      placeholder="Enter your phone number"
                      maxLength={10}
                      required
                    />
                    {phoneError && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {phoneError}
                      </p>
                    )}
                  </div>

                  {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-neutral-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-300"
                        placeholder="Your company name"
                      />
                    </div>
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-neutral-700 mb-2">
                        Service Interested In *
                      </label>
                      <select
                        id="service"
                        name="service"
                        required
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-300"
                      >
                        <option value="">Select a service</option>
                        {services.map((service) => (
                          <option key={service} value={service}>{service}</option>
                        ))}
                      </select>
                    </div>
                  </div> */}

                  {/* <div>
                    <label htmlFor="budget" className="block text-sm font-medium text-neutral-700 mb-2">
                      Project Budget
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-300"
                    >
                      <option value="">Select budget range</option>
                      {budgetRanges.map((range) => (
                        <option key={range} value={range}>{range}</option>
                      ))}
                    </select>
                  </div> */}

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-neutral-700 mb-2">
                      Project Details *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-300 resize-none"
                      placeholder="Tell us about your project, goals, and any specific requirements..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full btn-primary inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 15 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: false }}
            >
              <h2 className="text-3xl font-serif font-bold mb-8">Get in Touch</h2>

              <div className="space-y-8 mb-12">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: false }}
                    className="flex items-start space-x-4"
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-accent-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-neutral-900 mb-1">{info.title}</h3>
                      <p className="text-neutral-700 font-medium mb-1">{info.details}</p>
                      <p className="text-neutral-500 text-sm">{info.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Why Choose Us */}
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <h3 className="text-xl font-serif font-semibold mb-6">Why Choose Us?</h3>
                <ul className="space-y-4">
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-primary-500 flex-shrink-0" />
                    <span className="text-neutral-700">Free consultation and strategy session</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-primary-500 flex-shrink-0" />
                    <span className="text-neutral-700">Custom proposal tailored to your needs</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-primary-500 flex-shrink-0" />
                    <span className="text-neutral-700">No obligation, transparent pricing</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-primary-500 flex-shrink-0" />
                    <span className="text-neutral-700">Quick response within 24 hours</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-neutral-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Frequently Asked Questions</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Here are some common questions we receive from luxury brands looking to elevate their digital presence.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "What is Color Drop Business Solutions (CDBS)?",
                answer: "Color Drop Business Solutions (CDBS) is a leading digital marketing, creative, and IT solutions agency founded in 2013 by Jatin Puri. We help businesses grow, strengthen their brand identity, and achieve measurable results through a blend of creativity, technology, and strategic expertise. We are Central India's emerging digital marketing and business solutions company."
              },
              {
                question: "What services does CDBS offer?",
                answer: "We offer a complete suite of services including digital marketing, social media management, performance advertising, branding, graphic designing, business consulting, website development, and mobile app development. Our IT division focuses on creating fast, secure, user-friendly websites and apps tailored to each client's goals."
              },
              {
                question: "What is CDBS's process?",
                answer: "Our process is simple and effective: Understand. Create. Deliver. Grow. We start by understanding your business, goals, and requirements. Then we create tailored solutions, deliver them with excellence, and help you grow through continuous support and optimization. We believe in meaningful collaborations, transparent communication, and long-term success."
              },
              {
                question: "Who is the founder of CDBS?",
                answer: "Jatin Puri is the founder of Color Drop Business Solutions. He has deep expertise in marketing strategy, business consulting, brand development, and performance marketing. After representing India at ASME, USA in 2009 and working at HCL Technologies, he founded CDBS in 2013. His work has earned recognition from Meta for excellence in digital advertising."
              },
              {
                question: "What recognition has CDBS received?",
                answer: "CDBS has been recognized by Meta for excellence in ROI-based digital advertising in 2023. We continuously evolve with new tools, technologies, and industry trends, and have become Central India's trusted digital marketing and IT solutions company known for quality, innovation, and consistent performance."
              },
              {
                question: "What are CDBS's core values?",
                answer: "Our core values are Excellence (striving for perfection in every project), Passion (love for digital innovation), Collaboration (working closely with clients as partners), and Innovation (staying ahead of trends with cutting-edge technology). These principles guide everything we do."
              },
              {
                question: "What is CDBS's mission and vision?",
                answer: "Our mission is to empower businesses with creative, data-driven, and result-oriented digital solutions that enhance brand value, accelerate growth, and build long-term customer trust. Our vision is to become Central India's most trusted and innovative digital marketing company—where creativity meets technology, and every brand finds its unique identity and growth path."
              },
              {
                question: "How long has CDBS been in business?",
                answer: "CDBS was founded in 2013, making us over 12 years old. Our founder, Jatin Puri, has been in the industry since 2010, starting with HCL Technologies before launching CDBS. We have extensive experience helping businesses across various industries achieve digital transformation and growth."
              },
              {
                question: "What makes CDBS different from other agencies?",
                answer: "We combine technical expertise with creative innovation, backed by proven results and Meta recognition. Our founder's unique journey from global recognition to entrepreneurship brings a strategic perspective. We focus on long-term partnerships, transparent communication, and measurable results. Our process—Understand, Create, Deliver, Grow—ensures every project is tailored to your specific needs."
              },
              {
                question: "What industries does CDBS serve?",
                answer: "We work with businesses across various industries including retail & e-commerce, real estate, education, hospitality, food & beverages, health & wellness, beauty & personal care, finance, IT, NGOs, medical practitioners, construction, travel, and personal branding. We have helped scale businesses from startups to multi-location operations."
              }
            ].map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="bg-white rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-lg font-semibold text-neutral-900 mb-3">{faq.question}</h3>
                <p className="text-neutral-600 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-primary-500 to-accent-500">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
          >
            <h2 className="text-4xl font-serif font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Don't wait to elevate your digital presence. Contact us today for a free consultation.
            </p>
            <a href="tel:+919999206699" className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-accent-50 transition-colors duration-300 inline-flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              Call Now: +91 99992 06699
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
